/**
 * Theme Context - Dark/Light mode support
 * Persists preference in localStorage
 */
import React, { createContext, useContext, useState, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();

/* Shared palette values */
const shared = {
  primary: { main: '#2E7D32', light: '#4CAF50', dark: '#1B5E20' },
  success: { main: '#4CAF50', light: '#66BB6A' },
  error: { main: '#F44336', light: '#EF5350' },
  warning: { main: '#FF9800', light: '#FFA726' },
  info: { main: '#2196F3' },
};

const getTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      ...shared,
      ...(mode === 'light'
        ? {
            background: { default: '#F5F7FA', paper: '#FFFFFF' },
            text: { primary: '#1a1a2e', secondary: '#64748b' },
            divider: '#E2E8F0',
          }
        : {
            background: { default: '#0a1628', paper: '#111c32' },
            text: { primary: '#E2E8F0', secondary: '#94A3B8' },
            divider: '#1e2d45',
          }),
    },
    typography: {
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      h4: { fontWeight: 700 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: mode === 'light' ? '1px solid #E2E8F0' : '1px solid #1e2d45',
          },
        },
      },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    },
  });

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(localStorage.getItem('themeMode') || 'light');
  const theme = useMemo(() => getTheme(mode), [mode]);

  const toggleTheme = () => {
    const next = mode === 'light' ? 'dark' : 'light';
    setMode(next);
    localStorage.setItem('themeMode', next);
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeMode = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeProvider');
  return ctx;
};
